/**
 * SourceViewerPane — the right pane of the split-view results page.
 *
 * Header: "N docs" + a document picker (the detail response's `documents`
 * array). Body: the selected document rendered in an <iframe> from same-origin
 * bytes — GET /api/crm/documents/:id/file fetched as a blob → object URL (the
 * browser-native PDF viewer supplies toolbar/zoom; PNG/JPEG records render
 * fine too; no presigned PHI-bucket URL ever reaches the client). Blobs are
 * cached per document id so re-jumps don't refetch; every object URL is
 * revoked on unmount (the parent keys this pane by reviewId, so a review
 * change also resets everything).
 *
 * Clicking a DOCUMENT citation re-mounts the iframe with a `#page=n` fragment
 * (the view's nonce keys the iframe so a re-jump to the same doc forces a
 * reload). Clicking a NON-document citation swaps the body to a TEXT VIEW —
 * the dossier segment fetched from GET /eligibility-reviews/:id/segments/:n
 * (bytes proxied + audited server-side) with a "Back to documents" button.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { DossierSegment, EligibilityReviewDocument } from '../../types';
import { dateTime } from '../../lib';
import { Alert } from '../ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** Axios blob-response errors carry a Blob body — parse the JSON inside it. */
async function blobErrMsg(e: any): Promise<string> {
  const d = e?.response?.data;
  if (d instanceof Blob) {
    try {
      const p = JSON.parse(await d.text());
      return p.detail || p.error || errMsg(e);
    } catch {
      /* fall through */
    }
  }
  return errMsg(e);
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  document_page: 'Medical record page',
  communication: 'Communication',
  form_response: 'Form response',
  attempt: 'Prescreen attempt',
  profile: 'Medical profile',
  demographics: 'Demographics',
};

/** What the right pane is showing — owned by the results page. */
export type SourceView =
  | { kind: 'document'; documentId: string; page?: number; nonce: number }
  | { kind: 'segment'; segmentNumber: number };

interface Props {
  reviewId: string;
  documents: EligibilityReviewDocument[];
  view: SourceView | null;
  onSelectDocument: (documentId: string) => void;
  onBackToDocuments: () => void;
}

export default function SourceViewerPane({
  reviewId,
  documents,
  view,
  onSelectDocument,
  onBackToDocuments,
}: Props) {
  // ---- Document blobs (cached per document id, revoked on unmount) --------
  const blobCache = useRef<Map<string, string>>(new Map());
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);

  const docId = view?.kind === 'document' ? view.documentId : null;

  useEffect(() => {
    if (!docId) return;
    const cached = blobCache.current.get(docId);
    if (cached) {
      setDocUrl(cached);
      setDocLoading(false);
      setDocError(null);
      return;
    }
    let alive = true;
    setDocUrl(null);
    setDocLoading(true);
    setDocError(null);
    api.get(`/api/crm/documents/${docId}/file`, { responseType: 'blob' })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        // Cache even if this effect was superseded — unmount cleanup revokes.
        blobCache.current.set(docId, url);
        if (alive) setDocUrl(url);
      })
      .catch(async (e) => {
        const msg = await blobErrMsg(e);
        if (alive) setDocError(msg);
      })
      .finally(() => {
        if (alive) setDocLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [docId]);

  // Revoke every cached object URL when the pane unmounts (the parent keys
  // this component by reviewId, so a review change lands here too).
  useEffect(() => {
    const cache = blobCache.current;
    return () => {
      cache.forEach((url) => URL.revokeObjectURL(url));
      cache.clear();
    };
  }, []);

  // ---- Segment text view ----------------------------------------------------
  const segmentNumber = view?.kind === 'segment' ? view.segmentNumber : null;
  const [segment, setSegment] = useState<DossierSegment | null>(null);
  const [segLoading, setSegLoading] = useState(false);
  const [segError, setSegError] = useState<string | null>(null);

  useEffect(() => {
    if (segmentNumber == null) return;
    let alive = true;
    setSegment(null);
    setSegLoading(true);
    setSegError(null);
    api.get(`/api/crm/eligibility-reviews/${reviewId}/segments/${segmentNumber}`)
      .then((res) => { if (alive) setSegment(res.data?.segment || null); })
      .catch((e) => { if (alive) setSegError(errMsg(e)); })
      .finally(() => { if (alive) setSegLoading(false); });
    return () => { alive = false; };
  }, [reviewId, segmentNumber]);

  // ---- Render ---------------------------------------------------------------
  const selectedDoc = docId ? documents.find((d) => d.id === docId) || null : null;
  const docName = selectedDoc?.filename || 'Document';

  const segSource = segment?.source;
  const segSourceBits = segSource
    ? [
        SOURCE_TYPE_LABELS[segSource.type] || segSource.type,
        segSource.type === 'document_page' && segSource.page != null ? `p.${segSource.page}` : null,
        segSource.occurred_at ? dateTime(segSource.occurred_at) : null,
      ].filter(Boolean).join(' · ')
    : '';

  return (
    <div className="flex h-[70vh] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 lg:h-[calc(100vh-8rem)]">
      {/* Header bar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2.5 dark:border-gray-700">
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          <FileText size={12} />
          {documents.length} doc{documents.length !== 1 ? 's' : ''}
        </span>
        {documents.length > 0 && (
          <select
            value={docId && documents.some((d) => d.id === docId) ? docId : ''}
            onChange={(e) => { if (e.target.value) onSelectDocument(e.target.value); }}
            aria-label="Select document"
            className="min-h-[44px] min-w-0 flex-1 truncate rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 sm:min-h-0"
          >
            {(!docId || !documents.some((d) => d.id === docId)) && (
              <option value="" disabled>
                {view?.kind === 'segment' ? 'Viewing a text source…' : 'Select a document…'}
              </option>
            )}
            {documents.map((d) => (
              <option key={d.id} value={d.id}>
                {d.filename || 'Document'}
                {d.page_count ? ` (${d.page_count} p.)` : ''}
              </option>
            ))}
          </select>
        )}
        {view?.kind === 'segment' && documents.length > 0 && (
          <button
            type="button"
            onClick={onBackToDocuments}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
          >
            <ArrowLeft size={13} />
            Back to documents
          </button>
        )}
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 bg-gray-50 dark:bg-gray-900/40">
        {view?.kind === 'segment' ? (
          <div className="h-full overflow-y-auto p-4">
            {segLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : segError ? (
              <Alert tone="error" icon={false}>{segError}</Alert>
            ) : segment ? (
              <>
                <p className="text-xs font-medium uppercase tracking-wider text-primary-600 dark:text-primary-400">
                  Segment {segment.n}
                </p>
                <h3 className="mt-0.5 text-base font-semibold text-gray-900 dark:text-gray-100">
                  {segment.label || 'Source text'}
                </h3>
                {segSourceBits && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{segSourceBits}</p>
                )}
                <p className="mt-3 whitespace-pre-wrap break-words rounded-lg bg-white p-3 text-sm leading-relaxed text-gray-800 shadow-sm dark:bg-gray-800 dark:text-gray-200">
                  {segment.text}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">This source is unavailable.</p>
            )}
          </div>
        ) : view?.kind === 'document' ? (
          docLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : docError ? (
            <div className="p-4">
              <Alert tone="error" icon={false}>{docError}</Alert>
            </div>
          ) : docUrl ? (
            // The nonce keys the iframe so re-jumping to another page of the
            // SAME cached blob forces a fresh load (the #page fragment is only
            // honored at document load time).
            <iframe
              key={`${view.documentId}-${view.page ?? 0}-${view.nonce}`}
              src={`${docUrl}${view.page != null ? `#page=${view.page}` : ''}`}
              title={docName}
              className="h-full w-full border-0"
            />
          ) : null
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
            <FileText className="h-8 w-8 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No medical-record documents are on file for this lead.
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Citations from texts, calls and forms still open here as text.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
